// The attendee site's whole network surface.
//
// Deliberately smaller than admin-web's client: there is no Firebase SDK, no
// Authorization header, no 401 retry. Everything this site reaches lives under
// /v1/public, which the backend exempts from the auth gate — so a credential
// here would be a credential that should not exist.

/**
 * The API origin.
 *
 * `||`, not `??`. The deploy passes `VITE_API_BASE="${API_BASE_URL:-}"`, and
 * `.env.deploy.example` ships `API_BASE_URL=` empty with a note that it is
 * auto-populated after the backend deploy — so an EMPTY STRING is the default
 * state on a first deploy, and `??` does not catch it.
 *
 * With an empty base every request goes out relative, Hosting's `**` rewrite
 * answers `/v1/public/...` with index.html and a 200, `res.ok` is true, and the
 * page renders "Something went wrong at our end" while the deploy reports
 * success. Falling back to localhost instead fails fast and visibly, which is
 * the difference between a five-minute fix and an afternoon.
 */
const configuredBase = import.meta.env.VITE_API_BASE?.trim();
export const API_BASE = configuredBase || 'http://localhost:8081';

/** One entry of `error.details.fields` — the per-field validation contract. */
export interface FieldFault {
  code: string;
  message: string;
}

/**
 * Any non-2xx response.
 *
 * `reason` and `fields` are lifted out of `error.details` at construction so
 * callers branch on a value rather than digging through an unknown shape at
 * every call site. Both are absent unless the server sent them.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly reason: string | null;
  readonly fields: Record<string, FieldFault>;
  readonly data: unknown;

  constructor(status: number, data: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.code = readString(errorNode(data), 'code');
    this.reason = readString(detailsNode(data), 'reason');
    this.fields = readFields(detailsNode(data));
  }
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const errorNode = (data: unknown): Record<string, unknown> | null => {
  const body = asRecord(data);
  return body ? asRecord(body.error) : null;
};

const detailsNode = (data: unknown): Record<string, unknown> | null => {
  const err = errorNode(data);
  return err ? asRecord(err.details) : null;
};

const readString = (node: Record<string, unknown> | null, key: string): string | null => {
  const value = node?.[key];
  return typeof value === 'string' && value ? value : null;
};

// Own properties only, and `__proto__` / `constructor` / `prototype` skipped:
// this map is keyed by strings the server chose and is later spread into an
// object that React reads by key. A field literally named `__proto__` is not a
// realistic form key, but it is exactly the key that turns a lookup into an
// assignment somewhere downstream, and dropping it costs nothing.
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

const readFields = (details: Record<string, unknown> | null): Record<string, FieldFault> => {
  const raw = asRecord(details?.fields);
  if (!raw) return {};
  const out: Record<string, FieldFault> = Object.create(null);
  for (const key of Object.keys(raw)) {
    if (UNSAFE_KEYS.has(key)) continue;
    const entry = asRecord(raw[key]);
    const message = readString(entry, 'message');
    if (!message) continue;
    out[key] = { code: readString(entry, 'code') ?? 'invalid', message };
  }
  return out;
};

const extractMessage = (data: unknown, fallback: string): string =>
  readString(errorNode(data), 'message') ?? readString(asRecord(data), 'message') ?? fallback;

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  } catch (cause) {
    // A dropped connection is not an HTTP status, but every caller branches on
    // ApiError — giving it status 0 keeps the one error type and lets the UI
    // say "check your connection" instead of "something went wrong".
    throw new ApiError(0, null, (cause as Error)?.message || 'Network request failed');
  }

  const text = await res.text();
  let data: unknown = text;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    /* a non-JSON body is still the error message we have */
  }

  if (!res.ok) {
    throw new ApiError(res.status, data, extractMessage(data, `${res.status} ${res.statusText}`));
  }
  return (text ? data : undefined) as T;
};

export const api = {
  get: <T>(path: string, init?: RequestInit): Promise<T> => request<T>(path, init),
  post: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
};
